/**
 * Canonical URL Helper
 * Generates canonical URLs for different page types, stripping tracking parameters
 */

/**
 * Get the base URL of the current site
 * @returns {string} Base URL (e.g., https://example.com)
 */
export const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return '';
};

/**
 * Strip tracking parameters from URL
 * Removes utm_*, ref, gclid, fbclid, and other tracking parameters
 * @param {string} url - URL to clean
 * @returns {string} Cleaned URL
 */
export const stripTrackingParams = (url) => {
  if (!url) return '';
  
  try {
    const urlObj = new URL(url);
    const trackingParams = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'ref', 'gclid', 'fbclid', 'twclid', 'li_fat_id', '_ga', '_gid',
      'source', 'medium', 'campaign', 'term', 'content'
    ];
    
    trackingParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, return original
    return url;
  }
};

/**
 * Generate canonical URL for homepage
 * @returns {string} Canonical URL
 */
export const getHomepageCanonical = () => {
  return getBaseUrl() + '/';
};

/**
 * Generate canonical URL for restaurant/store page
 * @param {string} slug - Restaurant slug
 * @returns {string} Canonical URL
 */
export const getRestaurantCanonical = (slug) => {
  if (!slug) return getHomepageCanonical();
  return `${getBaseUrl()}/stores/${slug}`;
};

/**
 * Generate canonical URL for product/item page
 * @param {string} restaurantSlug - Restaurant slug
 * @param {string|number} productId - Product ID
 * @returns {string} Canonical URL
 */
export const getProductCanonical = (restaurantSlug, productId) => {
  if (!restaurantSlug || !productId) return getHomepageCanonical();
  return `${getBaseUrl()}/stores/${restaurantSlug}/${productId}`;
};

/**
 * Generate canonical URL for category page
 * @param {string} categorySlug - Category slug (optional)
 * @param {Object} queryParams - Query parameters to keep (optional)
 * @returns {string} Canonical URL
 */
export const getCategoryCanonical = (categorySlug = null, queryParams = {}) => {
  const baseUrl = getBaseUrl();
  let url = `${baseUrl}/categories/stores`;
  
  if (categorySlug) {
    const params = new URLSearchParams();
    params.set('category', categorySlug);
    
    // Add other important query params
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key]) {
        params.set(key, queryParams[key]);
      }
    });
    
    url += `?${params.toString()}`;
  }
  
  return url;
};

/**
 * Generate canonical URL for content page
 * @param {string} slug - Page slug
 * @returns {string} Canonical URL
 */
export const getContentPageCanonical = (slug) => {
  if (!slug) return getHomepageCanonical();
  return `${getBaseUrl()}/pages/${slug}`;
};

/**
 * Generate canonical URL for current page based on route
 * @param {string} pathname - Current pathname
 * @param {Object} params - Route parameters
 * @param {Object} queryParams - Query parameters to keep
 * @returns {string} Canonical URL
 */
export const getCurrentPageCanonical = (pathname, params = {}, queryParams = {}) => {
  const baseUrl = getBaseUrl();
  
  // Homepage
  if (pathname === '/' || pathname === '') {
    return getHomepageCanonical();
  }
  
  // Restaurant page: /stores/:restaurant
  if (pathname.startsWith('/stores/') && params.restaurant && !params.id) {
    return getRestaurantCanonical(params.restaurant);
  }
  
  // Product page: /stores/:restaurant/:id
  if (pathname.startsWith('/stores/') && params.restaurant && params.id) {
    return getProductCanonical(params.restaurant, params.id);
  }
  
  // Category page: /categories/stores
  if (pathname.startsWith('/categories/stores')) {
    return getCategoryCanonical(params.categorySlug || queryParams.category, queryParams);
  }
  
  // Content page: /pages/:slug
  if (pathname.startsWith('/pages/') && params.slug) {
    return getContentPageCanonical(params.slug);
  }
  
  // Relax routes (canonical is /relax; legacy /phase2 redirects here)
  if (pathname.startsWith('/relax/') || pathname.startsWith('/phase2/')) {
    // Relax product: /relax/product/:slug
    if (pathname.includes('/product/') && params.slug) {
      return `${baseUrl}/relax/product/${params.slug}`;
    }
    // Relax category: /relax/category/:slug
    if (pathname.includes('/category/') && params.slug) {
      return `${baseUrl}/relax/category/${params.slug}`;
    }
  }
  
  // Default: use current URL without tracking params
  const currentUrl = baseUrl + pathname;
  return stripTrackingParams(currentUrl);
};

/**
 * Get absolute URL from relative path
 * @param {string} path - Relative path
 * @returns {string} Absolute URL
 */
export const getAbsoluteUrl = (path) => {
  if (!path) return getBaseUrl();
  
  // If already absolute, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return stripTrackingParams(path);
  }
  
  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return getBaseUrl() + cleanPath;
};

